'use client'

import { ChevronRightIcon } from '@repo/ui/ChevronRightIcon'
import { ClickAwayListener } from '@mui/base/ClickAwayListener'
import { Link } from 'react-router-dom'
import { LinkWithClientMatterNo } from '@repo/app-assets/LinkWithClientMatterNo'
import { useEffect, useRef } from 'react'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import { useNavigationStore } from '@repo/store/useNavigationStore'
import { filterMenuItemsByVisibility } from '@repo/store/menuVisibilityFilters'
import { useWindowSize } from '@uidotdev/usehooks'
import MUIMenuItem from '@mui/material/MenuItem'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'

export const MainNavigationMenu = () => {
  const {
    mainMenuItems,
    showMenuHeading,
    rootMenuHeading,
    closeMainNavigationMenu,
    offsetTop,
    offsetLeft,
    offsetWidth,
    minMenuHeight,
    maxMenuHeight
  } = useMenuItemsStore()
  const { currentClientMatterNo } = useMattersStore()
  const { menuPanels, setMenuPanels, panelHeadings, setPanelHeadings, handleHover, mouseOverTimeout, open, setOpen } =
    useNavigationStore()
  const { height } = useWindowSize()
  const heightSufficientForHeading = height > 600
  const shouldShowHeading = showMenuHeading && heightSufficientForHeading
  const menuItemHeight = heightSufficientForHeading ? '40px' : '35px'
  const ref = useRef(null)

  useEffect(() => {
    if (!mainMenuItems) {
      setMenuPanels(null)
      setPanelHeadings(null)
      setOpen(false)
      return
    }

    setOpen(true)
    setMenuPanels([mainMenuItems])
  }, [mainMenuItems, setMenuPanels, setOpen, setPanelHeadings])

  useEffect(() => {
    if (!rootMenuHeading) {
      setPanelHeadings(null)
      return
    }

    setPanelHeadings([rootMenuHeading])
  }, [rootMenuHeading, setPanelHeadings])

  useEffect(() => {
    // close the menu if the user moves the mouse out of the menu
    const container = ref?.current
    if (container) container.addEventListener('mouseleave', closeMenu)

    return () => {
      if (container) {
        container.removeEventListener('mouseleave', closeMenu)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref])

  const closeMenu = () => {
    setMenuPanels(null)
    setPanelHeadings(null)
    closeMainNavigationMenu()
    setOpen(false)
  }

  const handleClick = (e, panelItem) => {
    e.stopPropagation()
    if (panelItem?.clickable && panelItem?.hasPermission) closeMenu()
  }

  const viewportWidth = window.innerWidth
  const panelWidth = viewportWidth < 1144 ? (viewportWidth - 120) / 4 : 256
  const maxWidth = panelWidth * 4
  const { menuYPosition, arrowYPosition } = calculateYPosition(offsetTop, maxMenuHeight)

  return (
      <ClickAwayListener onClickAway={closeMenu}>
        <MUIStack
          data-testid="MainNavigationMenu"
          id="MainNavigationMenu"
          direction="row"
          alignItems="stretch"
          justifyContent="flex-start"
          ref={ref}
          sx={{
            position: 'fixed',
            top: open ? menuYPosition : '-200000px',
            left: offsetLeft + offsetWidth,
            maxWidth: `${maxWidth}px`,
            zIndex: 10200,
            minHeight: `${minMenuHeight}px`,
            maxHeight: `${maxMenuHeight}px`,
            opacity: open ? 1 : 0,
            transition: 'opacity 200ms',
            background: `repeating-linear-gradient(
                to right,
                #ffffff,
                #ffffff ${maxWidth / 4}px,
                #fcfcfe ${maxWidth / 4}px,
                #fcfcfe ${maxWidth / 2}px,
                #f6F8Fb ${maxWidth / 2}px,
                #f6F8Fb ${(maxWidth * 3) / 4}px,
                #f5f6f9 ${(maxWidth * 3) / 4}px,
                #f5f6f9 100%
              )`,
            borderRadius: 1,
            boxShadow:
              '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)'
          }}
          elevation={24}
        >
          {showMenuHeading ? (
            <div
              data-testid="arrow"
              style={{
                backgroundColor: 'white',
                width: '12px',
                height: '12px',
                transform: 'rotate(45deg)',
                position: 'absolute',
                top: arrowYPosition,
                left: '-5px',
                pointerEvents: 'none'
              }}
            />
          ) : null}
          {menuPanels?.map((panel, index) => {
            // Apply visibility filters to the panel
            const filteredPanel = filterMenuItemsByVisibility(panel)
            const key = filteredPanel && filteredPanel[0] && filteredPanel[0].urlSegment ? filteredPanel[0].urlSegment : 'empty'

            return (
              <MUIPaper
                key={`panel-${key}-${index}`}
                sx={{
                  padding: '8px',
                  paddingTop: shouldShowHeading ? '0' : '8px',
                  width: `${panelWidth}px`,
                  minHeight: `${minMenuHeight}px`,
                  height: '100% !important',
                  backgroundColor: 'transparent'
                }}
                elevation={0}
              >
                {renderHeading(shouldShowHeading, panelHeadings, index)}
                {filteredPanel.map((panelItem) => {
                  const allowed = panelItem.hasPermission || panelItem.hasPermittedChildren

                  return (
                    <MUIMenuItem
                      sx={{
                        width: '100%',
                        height: menuItemHeight,
                        minHeight: menuItemHeight,
                        justifyContent: 'space-between',
                        userSelect: 'none',
                        cursor: panelItem.clickable && panelItem.hasPermission ? 'pointer' : 'auto',
                        borderRadius: '4px',
                        mb: 0,
                        padding: 0
                      }}
                      onMouseOver={() => handleHover(panelItem, index)}
                      onMouseOut={() => clearTimeout(mouseOverTimeout)}
                      onClick={(e) => handleClick(e, panelItem)}
                      key={panelItem.route}
                      disabled={!allowed}
                    >
                      {getMenuItemContents(panelItem, menuItemHeight, closeMenu, currentClientMatterNo)}
                    </MUIMenuItem>
                  )
                })}
                <MUIStack
                  data-testid="spacerToEnsureHoverWorksAsExpected"
                  sx={{
                    height: '100%',
                    width: '10px',
                    background: 'transparent',
                    position: 'absolute',
                    top: 0,
                    left: '-10px',
                    zIndex: -1
                  }}
                />
              </MUIPaper>
            )
          })}
        </MUIStack>
      </ClickAwayListener>
  )
}

const getMenuItemContents = (panelItem, menuItemHeight, closeMenu, currentClientMatterNo) => {
  const cursor = panelItem.clickable && panelItem.hasPermission ? 'pointer' : 'auto'

  const style = {
    width: '100%',
    height: menuItemHeight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    textDecoration: 'none',
    color: 'rgba(5, 28, 51, 0.66)',
    padding: '0 12px',
    userSelect: 'none',
    cursor
  }

  const handleActionClick = (e, action) => {
    console.log({ action })
    e.stopPropagation()
    e.preventDefault()
    action()
    closeMenu()
  }

  const contents = (
    <>
      <MUITypography sx={{ mb: 0, color: 'text.secondary', userSelect: 'none', cursor }} variant="body1" noWrap>
        {panelItem.label}
      </MUITypography>
      {panelItem.children ? (
        <ChevronRightIcon sx={{ height: '24px !important', mr: -2, color: 'rgba(5, 28, 51, 0.66)' }} />
      ) : null}
    </>
  )

  if (panelItem.clickable && panelItem.hasPermission) {
    if (panelItem.action) {
      return (
        <div style={style} onClick={(e) => handleActionClick(e, panelItem.action)}>
          {contents}
        </div>
      )
    }

    let to = panelItem.href || panelItem.redirectTo || panelItem.route || null
    let useLinkWithClientMatterNo = false

    // add clientMatterNo or clientNo if applicable

    if (currentClientMatterNo) {
      switch (to) {
        case 'case-management/clients/client-workspace':
        case 'case-management/clients/client-page':
        case 'case-management/clients/client-detail/company':
        case 'case-management/clients/client-detail/individual':
        case 'case-management/clients/client-detail/edit-client':
        case 'case-management/matters/matter-workspace':
        case 'case-management/matters/matter-history':
        case 'case-management/matters/matter-archive':
        case 'case-management/matters/matter-page':
        case 'case-management/matters/matter-detail':
        case 'case-management/matters/matter-detail/edit-matter':
        case 'case-management/matters/key-dates':
        case 'case-management/matters/document-production':
        case 'case-management/matters/workflows/run':
        case 'accounts/client-ledger/requisition':
          useLinkWithClientMatterNo = true
          break
      }
    }

    const target = panelItem.href ? '_blank' : '_self'

    if (useLinkWithClientMatterNo) {
      return (
        <LinkWithClientMatterNo clientMatterNo={currentClientMatterNo} to={to} style={style} target={target}>
          {contents}
        </LinkWithClientMatterNo>
      )
    }

    return (
      <Link to={to} style={style} target={target}>
        {contents}
      </Link>
    )
  }

  return <div style={style}>{contents}</div>
}

const renderHeading = (shouldShowHeading, headings, index) => {
  if (!shouldShowHeading || !headings || !headings[index]) return null

  const heading = headings[index]

  return (
    <MUIStack
      alignItems="center"
      direction="row"
      sx={{
        height: '60px',
        padding: '0 8px',
        borderBottom: '1px solid #051C331F',
        cursor: 'auto',
        mb: 2
      }}
    >
      <MUITypography variant="h6" sx={{ mb: 0, userSelect: 'none' }}>
        {heading}
      </MUITypography>
    </MUIStack>
  )
}

const calculateYPosition = (offsetTop, maxMenuHeight) => {
  const viewportHeight = window.innerHeight
  const bottomOfTallestMenu = offsetTop + maxMenuHeight

  let menuYPosition = offsetTop
  let arrowYPosition = 25

  if (bottomOfTallestMenu > viewportHeight) {
    menuYPosition = viewportHeight - maxMenuHeight - 10
  }

  if (menuYPosition < 0) {
    menuYPosition = 10
  }

  if (menuYPosition !== offsetTop) {
    arrowYPosition = offsetTop - menuYPosition + 25
  }

  return { menuYPosition, arrowYPosition }
}
